import React from 'react';
import { Link } from 'react-router-dom';

import styled from 'styled-components';

import { isObject, sortByOrder } from 'utils/helpers/object';

import InsightFieldRenderer from './InsightFieldRenderer';
import { AIInsightSolution } from '../../types/recommendations';

const ParagraphTitle = styled.div`
  font-size: 18px;
  font-family: var(--font-bold);
  margin-top: 20px;
  margin-bottom: 14px;
`;

const SubParagraphTitle = styled.div`
  font-size: 16px;
  font-family: var(--font-bold);
  margin-bottom: 9px;
`;

const INDENT_SIZE = 20;

const PaddedDiv = styled.div<{ indentation?: number | null }>`
  padding-left: ${({ indentation }) => (indentation ? `${INDENT_SIZE * indentation}px` : 'unset')};
`;

const KEYS_WITH_DEDICATED_RENDERERS = new Set([
  'title',
  'summary',
  'category',
  'score',
  'ranking',
  'community_score',
  'competitor_average_score',
  'criteria',
  'ranking_table',
  'scoring_results_table',
  'strengths',
  'weaknesses',
  'key_competitive_differences',
  'recommended_actions',
]);

const renderTitleWithCommunityLink = (
  rawTitle: string | undefined,
  communityId: number | undefined,
  communityName: string | undefined
): React.ReactNode => {
  if (!rawTitle) return null;
  if (!communityId || !communityName || !rawTitle.includes('[Community Name]')) {
    return rawTitle;
  }
  const [before, after] = rawTitle.split('[Community Name]');
  return (
    <>
      {before}
      <Link to={`/community/${communityId}`}>{communityName}</Link>
      {after}
    </>
  );
};

type Props = {
  structure: AIInsightSolution;
  communityId?: number;
  communityName?: string;
};

const RemainingInsightSections = ({ structure, communityId, communityName }: Props) => {
  const sortedInsights = sortByOrder(Object.entries(structure));

  return (
    <>
      {sortedInsights.map(([key, field]) => {
        if (KEYS_WITH_DEDICATED_RENDERERS.has(key)) {
          return null;
        }

        const value = field.value;

        return (
          <div key={key}>
            <ParagraphTitle>{renderTitleWithCommunityLink(field.title, communityId, communityName)}</ParagraphTitle>
            <PaddedDiv indentation={field.indentation_level}>
              {isObject(value) ? (
                <div>
                  {sortByOrder(Object.entries(value)).map(([subKey, subField]) => (
                    <div key={subKey}>
                      {isObject(subField) && subField.title ? (
                        <SubParagraphTitle>{String(subField.title)}</SubParagraphTitle>
                      ) : null}
                      <InsightFieldRenderer value={isObject(subField) ? subField.value : subField} />
                    </div>
                  ))}
                </div>
              ) : (
                <InsightFieldRenderer value={value} />
              )}
            </PaddedDiv>
          </div>
        );
      })}
    </>
  );
};

export default RemainingInsightSections;
